/** MI-1/8: advice and selections only; consent stays in LocalAiIntake. */
import { requestJson, responseRecord, responseArray } from './http';
import { listProviderKeys } from '../pages/ProviderKeys/providerKeysApi';

export interface AdviceItem { key: string; target_device: string; target_runtime: string; target_store_label: string }
export interface AdvisorPrefill { model_id: string; device_id: string; store_id: string }
export interface FitRow { key: string; fit: 'installable' | 'browse_only' | 'no_fit'; reasons: string[];
  runtime: string | null; store_label: string | null; browse_url: string }
export interface Advice { status: string; ranked_by: 'deterministic' | 'system_one'; note: string;
  items: { key: string; explanation: string }[]; table: FitRow[] }
export interface AssistantPlan { status: string; message: string; note: string; items: AdviceItem[] }
export interface ModelConnection { provider: string; model: string; vault_key_id: string }
export interface AdvisorConnection { provider: string; key_id: string; label: string }
const BASE = '/v1/local-ai/advisor';
function selection(device: string) { return `?device=${encodeURIComponent(device)}`; }
function string(value: unknown): string {
  if (typeof value !== 'string') throw new Error('Advisor response unavailable');
  return value;
}
/** Existing masked metadata only; the key client has no secret read operation. */
export async function fetchAdvisorConnections(): Promise<AdvisorConnection[]> {
  const payload = responseRecord(await listProviderKeys());
  return responseArray(payload, 'provider_keys').map(value => {
    const row = responseRecord(value);
    return { provider: string(row.provider), key_id: string(row.key_id),
      label: typeof row.label === 'string' ? row.label : string(row.provider) };
  });
}
export async function fetchLocalModelAdvice(device: string): Promise<Advice> {
  const data = responseRecord(await requestJson(`${BASE}/recommendations${selection(device)}`, { method: 'GET' }));
  return { status: string(data.status), ranked_by: data.ranked_by === 'system_one' ? 'system_one' : 'deterministic',
    note: string(data.note), items: responseArray(data.items).map(value => {
      const row = responseRecord(value); return { key: string(row.key), explanation: string(row.explanation) };
    }), table: responseArray(data.table).map(value => {
      const row = responseRecord(value);
      if (!['installable', 'browse_only', 'no_fit'].includes(String(row.fit))) throw new Error('Invalid fit');
      return { key: string(row.key), fit: row.fit as FitRow['fit'], reasons: responseArray(row.reasons).map(string),
        runtime: row.runtime === null ? null : string(row.runtime),
        store_label: row.store_label === null ? null : string(row.store_label), browse_url: string(row.browse_url) };
    }) };
}
export async function localModelAssistantTurn(device: string, conversation: string, connection?: ModelConnection): Promise<AssistantPlan> {
  const row = responseRecord(await requestJson(`${BASE}/assistant/turn${selection(device)}`, {
    method: 'POST', body: JSON.stringify({ conversation, ...(connection ? { connection } : {}) }),
  }));
  return { status: string(row.status), message: string(row.message), note: string(row.note),
    items: responseArray(row.items).map(value => {
      const item = responseRecord(value); return { key: string(item.key), target_device: string(item.target_device),
        target_runtime: string(item.target_runtime), target_store_label: string(item.target_store_label) };
    }) };
}
export async function prepareLocalModelPlan(device: string, items: AdviceItem[]): Promise<AdvisorPrefill[]> {
  const row = responseRecord(await requestJson(`${BASE}/plan${selection(device)}`, { method: 'POST', body: JSON.stringify({ items }) }));
  return responseArray(row.prefill).map(value => {
    const item = responseRecord(value); return { model_id: string(item.model_id), device_id: string(item.device_id), store_id: string(item.store_id) };
  });
}
